/**
 * How a scan's manual feedback was taken: who held the tape, what the client
 * wore, and the method behind a reported body fat value. Saved with the
 * confirmed readings, by the same PATCH (functions-api #22).
 */

/** As the API stores it. A coach entry carries the account from the token. */
export type MeasuredBy =
  | { kind: 'coach'; account?: string; email?: string }
  | { kind: 'other'; name: string };

export interface FeedbackContext {
  measuredBy: MeasuredBy;
  clothing?: string;
  bodyFatMethod?: string;
  updatedAt?: string;
}

export interface Choice {
  value: string;
  label: string;
}

export const CLOTHING_CHOICES: Choice[] = [
  { value: 'close-fitting', label: 'Close-fitting' },
  { value: 'loose', label: 'Loose' },
  { value: 'underwear', label: 'Underwear' },
];

export const BODY_FAT_METHODS: Choice[] = [
  { value: 'inbody', label: 'InBody' },
  { value: 'dexa', label: 'DEXA' },
  { value: 'bodpod', label: 'Bod Pod' },
  { value: 'skinfold', label: 'Skinfold calipers' },
  { value: 'bia', label: 'Bioimpedance scale (other)' },
];

/** The select value that opens the free-text field. */
export const OTHER = 'other';

/** Longest free text the API accepts. */
export const MAX_TEXT = 80;

/**
 * What the dialog edits. A choice field holds an option's value, OTHER with
 * the words in `…Text`, or '' for nothing chosen.
 */
export interface ContextDraft {
  measuredBy: 'coach' | 'other';
  measurerName: string;
  clothing: string;
  clothingText: string;
  bodyFatMethod: string;
  bodyFatMethodText: string;
}

const tidy = (s: string) => s.trim().replace(/\s+/g, ' ');

/** A stored free-text answer goes back into the text box, under Other. */
const splitChoice = (stored: string | undefined, choices: Choice[]) => {
  if (!stored) return { choice: '', text: '' };
  return choices.some((c) => c.value === stored)
    ? { choice: stored, text: '' }
    : { choice: OTHER, text: stored };
};

export const draftFrom = (stored: FeedbackContext | undefined, lastName = ''): ContextDraft => {
  const clothing = splitChoice(stored?.clothing, CLOTHING_CHOICES);
  const method = splitChoice(stored?.bodyFatMethod, BODY_FAT_METHODS);
  return {
    measuredBy: stored?.measuredBy.kind ?? 'coach',
    measurerName: stored?.measuredBy.kind === 'other' ? stored.measuredBy.name : lastName,
    clothing: clothing.choice,
    clothingText: clothing.text,
    bodyFatMethod: method.choice,
    bodyFatMethodText: method.text,
  };
};

const fromChoice = (choice: string, text: string) =>
  choice === OTHER ? tidy(text) || undefined : choice || undefined;

/** The body fat method the draft names, or undefined. */
export const methodOf = (d: ContextDraft) => fromChoice(d.bodyFatMethod, d.bodyFatMethodText);

/** What is sent. The API fills in the coach's account itself. */
export const toPayload = (d: ContextDraft, hasBodyFat: boolean): FeedbackContext => {
  const clothing = fromChoice(d.clothing, d.clothingText);
  const method = hasBodyFat ? methodOf(d) : undefined;
  return {
    measuredBy: d.measuredBy === 'coach' ? { kind: 'coach' } : { kind: 'other', name: tidy(d.measurerName) },
    ...(clothing ? { clothing } : {}),
    ...(method ? { bodyFatMethod: method } : {}),
  };
};

/**
 * Why the draft cannot be saved yet, or null. The body fat rule is the API's
 * own (functions-api #22): a value set or changed here needs a method.
 */
export const problemWith = (
  d: ContextDraft,
  bodyFat: { before?: number; after?: number },
): string | null => {
  if (d.measuredBy === 'other' && !tidy(d.measurerName)) return 'Enter who took the measurements.';
  if ([d.measurerName, d.clothingText, d.bodyFatMethodText].some((t) => tidy(t).length > MAX_TEXT)) {
    return `Keep text to ${MAX_TEXT} characters.`;
  }
  if (d.clothing === OTHER && !tidy(d.clothingText)) return 'Describe the clothing, or pick an option.';
  const changed = bodyFat.after !== undefined && bodyFat.after !== bodyFat.before;
  if (changed && !methodOf(d)) return 'Pick how the body fat was measured.';
  return null;
};

/** Whether the draft says something different from what is stored. */
export const contextChanged = (
  d: ContextDraft,
  stored: FeedbackContext | undefined,
  hasBodyFat: boolean,
): boolean => {
  const next = toPayload(d, hasBodyFat);
  // Nothing stored: only an answer beyond the default "Me" is a change, or
  // every old scan would open with Confirm changes already lit.
  if (!stored) return next.measuredBy.kind === 'other' || !!next.clothing || !!next.bodyFatMethod;
  const same =
    next.measuredBy.kind === stored.measuredBy.kind &&
    (next.measuredBy.kind === 'coach' ||
      (stored.measuredBy.kind === 'other' && next.measuredBy.name === stored.measuredBy.name)) &&
    next.clothing === stored.clothing &&
    next.bodyFatMethod === stored.bodyFatMethod;
  return !same;
};

const LAST_NAME_KEY = 'sizewall.feedback.lastMeasurer';

/** The name typed last time — in the study it is usually the same person. */
export const lastMeasurerName = (): string => {
  try {
    return localStorage.getItem(LAST_NAME_KEY) ?? '';
  } catch {
    return '';
  }
};

export const rememberMeasurerName = (name: string) => {
  try {
    localStorage.setItem(LAST_NAME_KEY, tidy(name));
  } catch {
    // A browser that refuses storage just types the name again.
  }
};
